import { LocationItem } from './types';
import { getProvinceWithAmphur, getLocationType, getProjectOnlyData, getLocationByLocationTypeID, getLocationBranchByLocationID } from '../../utils/requests';

// ฟังก์ชันสำหรับแปลงตัวเลขเป็นรูปแบบเงินบาทไทย
export const formatThaiCurrency = (value: number): string => {
  if (value === 0) return '0';
  
  return new Intl.NumberFormat('th-TH', {
    maximumFractionDigits: 0,
  }).format(value);
};

// แปลงข้อมูลสถานที่ให้อยู่ในรูปแบบที่ใช้งานได้
export const formatLocationData = (data: any): LocationItem[] => {
  if (!data || !data.data) {
    console.log("formatLocationData: ไม่มีข้อมูล data หรือ data.data", data);
    return [];
  }
  
  // กรองและแปลงข้อมูล
  const formattedData = data.data.map((item: any) => {
    // สร้าง object ชั่วคราวเพื่อตรวจสอบค่า
    const result = {
      label: item.Name_TH || item.PROVINCE_NAME_TH || item.location_type_name_TH || item.branch_name_TH || item.name || item.label ||"ไม่มีชื่อ",
      value: item.value || item.ID || item.PROVINCE_ID || item.location_type_ID || item.branch_id || 0,
      lat: item.LAT || item.PROVINCE_LAT || item.branch_lat,
      lng: item.LONG || item.PROVINCE_LON || item.branch_lng,
      children: item.children || null,
      typeId: item.location_type_ID || null
    };
    
    // ถ้า label เป็น undefined ให้แสดง log
    if (!result.label || result.label === "ไม่มีชื่อ") {
      console.log("พบ item ที่ไม่มี label:", item);
    }
    
    // กำหนดให้ "ไม่มีชื่อ" หรือ "ทั้งหมด" ไม่มี children เสมอ
    if (result.label === "ไม่มีชื่อ" || result.label === "ทั้งหมด") {
      result.children = null;
    }
    
    // ตรวจสอบกรณี children มีข้อมูลแต่มี label เป็นค่าว่าง
    if (result.children && Array.isArray(result.children)) {
      // ตรวจสอบว่ามี children ที่มี label เป็นค่าว่างหรือไม่
      const hasEmptyLabel = result.children.some(child => !child.label || child.label === "" || child.label === "ไม่มีชื่อ");
      // ถ้าทุก children มี label เป็นค่าว่าง ให้ถือว่าไม่มี children
      const allEmptyLabel = result.children.every(child => !child.label || child.label === "" || child.label === "ไม่มีชื่อ");
      
      if (allEmptyLabel) {
        console.log(`พบข้อมูล ${result.label} มี children ที่ไม่มี label ทั้งหมด ตั้งค่า children = null`);
        result.children = null;
      } else if (hasEmptyLabel) {
        // ถ้ามีบางรายการที่ไม่มี label ให้กรองออก
        console.log(`พบข้อมูล ${result.label} มี children บางรายการที่ไม่มี label กรองออก`);
        result.children = result.children.filter(child => child.label && child.label !== "" && child.label !== "ไม่มีชื่อ");
        
        // ถ้าหลังกรองแล้วไม่มีรายการเหลือ ให้ตั้งค่า children = null
        if (result.children.length === 0) {
          result.children = null;
        }
      } else {
        // มี children ที่มี label ถูกต้องทั้งหมด
        console.log(`พบข้อมูล ${result.label} มี children ที่มี label ถูกต้องทั้งหมด จำนวน ${result.children.length} รายการ`);
      }
    }
    
    return result;
  });
  
  // รวมผลลัพธ์พิเศษ (ทั้งหมด, ไม่มีชื่อ) ไว้ด้านบนสุด
  const specialItems = formattedData.filter(item => item.label === "ไม่มีชื่อ" || item.label === "ทั้งหมด");
  const regularItems = formattedData.filter(item => item.label !== "ไม่มีชื่อ" && item.label !== "ทั้งหมด");
  
  return [...specialItems, ...regularItems];
};

// ดึงข้อมูลจังหวัด
export const getProvinceData = async (locale = 'th') => {
  try {
    const response = await getProvinceWithAmphur(locale);
    return formatLocationData(response);
  } catch (error) {
    console.error('Error fetching province data:', error);
    return [];
  }
};

// ดึงข้อมูลประเภทสถานที่
export const getLocationTypeData = async (locale = 'th') => {
  try {
    const response = await getLocationType(locale);
    return formatLocationData(response);
  } catch (error) {
    console.error('Error fetching location type data:', error);
    return [];
  }
};

// ดึงข้อมูลโครงการ
export const getProjectData = async (categoryId: any, searchText: string, locale = 'th') => {
  try {
    const response = await getProjectOnlyData(categoryId, searchText);
    return formatLocationData(response);
  } catch (error) {
    console.error('Error fetching project data:', error);
    return [];
  }
};

// ดึงข้อมูลตามประเภทสถานที่
export const loadLocationData = async (typeId: number, locale = 'th') => {
  try {
    const response = await getLocationByLocationTypeID(`${typeId}`, locale);
    return formatLocationData(response);
  } catch (error) {
    console.error('Error loading location data:', error);
    return [];
  }
};

// ดึงข้อมูลสาขาของสถานที่
export const loadLocationBranches = async (locationId: string | number) => {
  try {
    // ตรวจสอบว่า locationId มีค่าหรือไม่
    if (!locationId) {
      console.warn('loadLocationBranches: ไม่มี locationId');
      return [];
    }
    
    console.log(`loadLocationBranches: เริ่มโหลดข้อมูลสาขาสำหรับ ID: ${locationId}`);
    const response = await getLocationBranchByLocationID(`${locationId}`);
    
    // ตรวจสอบว่า response มีค่าหรือไม่
    if (!response || !response.data) {
      console.warn('loadLocationBranches: ไม่มีข้อมูลใน response หรือ response.data');
      return [];
    }
    
    // ตรวจสอบและแสดง log จำนวนรายการทั้งหมด
    console.log(`loadLocationBranches: พบข้อมูลทั้งหมด ${response.data.length} รายการ`);
    
    // แสดง log ข้อมูลที่ได้รับมา
    if (response.data.length > 0) {
      console.log('loadLocationBranches: ตัวอย่างข้อมูลแรก:', response.data[0]);
    }
    
    // กรองรายการที่ชื่อ "ไม่มีชื่อ" หรือ "ทั้งหมด" หรือค่าว่าง ออกจากผลลัพธ์
    const filteredData = response.data.filter((item: any) => {
      const name = item.Name_TH || item.PROVINCE_NAME_TH || item.location_type_name_TH || item.branch_name_TH || item.name || item.label || "ไม่มีชื่อ";
      return name !== "ไม่มีชื่อ" && name !== "ทั้งหมด" && name !== "";
    });
    
    // ตรวจสอบและแสดง log จำนวนรายการหลังกรอง
    console.log(`loadLocationBranches: เหลือข้อมูลหลังกรอง ${filteredData.length} รายการ`);
    
    // ถ้าไม่มีข้อมูลเหลือหลังกรอง ให้ส่งคืน array ว่าง
    if (filteredData.length === 0) {
      console.log('loadLocationBranches: ไม่มีข้อมูลเหลือหลังกรอง ส่งคืน array ว่าง');
      return [];
    }
    
    // สร้าง response ใหม่ด้วยข้อมูลที่กรองแล้ว
    const filteredResponse = {
      ...response,
      data: filteredData
    };
    
    // แปลงข้อมูลและตรวจสอบผลลัพธ์
    const result = formatLocationData(filteredResponse);
    console.log(`loadLocationBranches: ผลลัพธ์สุดท้าย ${result.length} รายการ`);
    
    if (result.length > 0) {
      console.log('loadLocationBranches: ตัวอย่างผลลัพธ์แรก:', result[0]);
    }
    
    return result;
  } catch (error) {
    console.error('Error loading location branches:', error);
    return [];
  }
};

// ฟังก์ชันสำหรับฟิลเตอร์ Cascader
export const filterCascaderOptions = (inputValue: string, path: any[]) => {
  return path.some(option => option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
}; 